import React from 'react';
import { ArrowRight } from 'react-feather';
import { DesignHeader } from './DesignHeader';
import { PageTitle } from '../ui/PageTitle';
import { LeadParagraph } from '../ui/LeadParagraph';
import { BodyParagraph } from '../ui/BodyParagraph';
import { HighlightTag } from '../ui/HighlightTag';
import { ContentLayout } from '../ui/ContentLayout';
import { ExternalLink } from '../ui/ExternalLink';
import { ImageModule } from '../ui/ImageModule';
import { PageMeta } from './PageMeta';
import { OG_IMAGE } from '../constants/assets';
export function DesignContent({
  activePage,
  onNavigate,
  onOpenMobileMenu
}: {
  activePage: string;
  onNavigate: (page: string) => void;
  onOpenMobileMenu: () => void;
}) {
  const renderContent = () => {
    switch (activePage) {
      case 'research':
        return <>
            <PageMeta title="Turning research into decisions" description="I integrate discovery directly into delivery cycles, turning qualitative signals into prioritised decisions that shape what gets built." url="https://mintextract.design/research" ogImage={OG_IMAGE} data-id="element-74" />
            <PageTitle data-id="element-75">
              Research is only useful if it changes what gets built
            </PageTitle>
            <div className="space-y-8" data-id="element-76">
              <LeadParagraph data-id="element-77">
                I integrate discovery directly into delivery cycles, rather than
                seeing it as a preliminary step before actual work.
              </LeadParagraph>
              <BodyParagraph data-id="element-78">
                At Lendi Group, this meant fortnightly field studies embedded in
                sprint cycles, including a June 2024 study where two other
                designers and I spent 23 hours with 30 brokers and support
                staff, generating 384 feedback points that directly shaped
                product decisions. I intentionally involve engineers in these
                sessions because the empathy built during contextual interviews
                changes how technical trade-offs are made during sprint
                planning.
              </BodyParagraph>
              <div className="my-12 animate-fadeIn" data-id="element-79">
                <div className="rounded-lg overflow-hidden" style={{
                aspectRatio: '4/3'
              }} data-id="element-80">
                  <img src="https://cdn.magicpatterns.com/uploads/e8HGASn8AXvEBkhrV9NWbQ/Slide_2.svg" alt="Broker Field Study — 384 feedback points collected from 30 brokers in a single study" className="w-full h-full object-cover" data-id="element-81" />
                </div>
              </div>
              <BodyParagraph data-id="element-82">
                At Shift, research informs a bigger question: how behavioural
                and operational signals, such as time in queue, customer and
                broker activity patterns, and system integration failures,
                predict which deals are at risk of abandonment before they
                stall? The goal is to move from reactive UX fixes to a
                predictive, data-driven model that steers different customer
                archetypes toward the right product at the right time, with
                minimal friction. Abandonment isn't a front-end problem; it's
                about queue design, workflow intelligence, and partner dynamics.
              </BodyParagraph>
              <BodyParagraph data-id="element-83">
                The Opportunity Solution Tree keeps this work honest,
                translating messy qualitative signals into a prioritised
                structure that product and business teams can interrogate and
                challenge, rather than just receive.
              </BodyParagraph>
              <BodyParagraph data-id="element-84">
                I've also introduced AI-assisted workflows to change how
                insights move through an organisation, not just how interfaces
                are made. At Lendi Group, that meant a passive idea bank in Jira
                Product Discovery to capture and categorise broker feedback at
                scale, and a Rovo AI agent generating regular report summaries
                to guide refinement priorities. For prototyping, MagicPatterns
                and v0 compress the gap between a rough hypothesis and a
                testable outcome, and extract acceptance criteria directly from
                design artefacts.{' '}
                <HighlightTag data-id="element-85">
                  The goal across all of it is the same: reduce the friction
                  between a signal and a decision.
                </HighlightTag>
              </BodyParagraph>
            </div>
          </>;
      case 'systems':
        return <>
            <PageMeta title="Building systems that last" description="I anchor work in real workflows and measurable outcomes, so it holds its shape after handover, headcount changes, and shifting priorities." url="https://mintextract.design/systems" ogImage={OG_IMAGE} data-id="element-86" />
            <PageTitle data-id="element-87">
              Good delivery is measured by what remains after you leave
            </PageTitle>
            <div className="space-y-8" data-id="element-88">
              <LeadParagraph data-id="element-89">
                I anchor work in real workflows and measurable outcomes from the
                start, so it holds its shape after handover, headcount changes,
                and shifting priorities.
              </LeadParagraph>
              <BodyParagraph data-id="element-90">
                At Shift, I defined the product concept and cross-functional
                scope for Property Hub, a single source of truth for end-to-end
                lending efficiency. This work was grounded in detailed process
                mapping across Credit, Business Services, Settlements, and
                Collections, with measurable outcomes set from the start: time
                saved per assessment, reduced platform switches (aiming to
                decrease from four sources to two per credit assessment),
                automated equity calculation, and more reliable data as primary
                success indicators.{' '}
                <HighlightTag data-id="element-91">
                  Anchoring the product in real workflows rather than feature
                  requests gives it a sustainable long-term direction.
                </HighlightTag>
              </BodyParagraph>
              <BodyParagraph data-id="element-92">
                At Lendi Group, the{' '}
                <ExternalLink href="https://www.theadviser.com.au/broker/46899-lendi-group-launches-sandbox-platform-to-support-brokers?highlight=WyJzYW5kYm94Il0=" data-id="element-93">
                  Servicing Sandbox
                </ExternalLink>{' '}
                launched across the network in March 2025, following a pilot
                with 100 staff in November 2024. I used feature maps at the user
                story level to explore variations and edge cases early,
                validating assumptions through that pilot before full rollout.
                In the first week, 1,100 brokers ran 7,000 scenarios, with
                nearly 70% returning.
              </BodyParagraph>
              <div className="my-12 animate-fadeIn" data-id="element-94">
                <div className="rounded-lg overflow-hidden" style={{
                aspectRatio: '4/3'
              }} data-id="element-95">
                  <img src="https://cdn.magicpatterns.com/uploads/u6sZKtUbYGfjrCMCMEnRQA/Slide_3.svg" alt="Servicing Sandbox — 7,000+ scenarios run by 1,100 brokers in week one, with nearly 70% returning" className="w-full h-full object-cover" data-id="element-96" />
                </div>
              </div>
              <BodyParagraph data-id="element-97">
                One thing I'd do differently at Lendi is build organisational
                influence sooner. That lesson now shapes how I engage
                stakeholders, including my current efforts to develop strategic
                partnerships with Shift's senior leadership to influence
                workflow, prioritisation, and the product experience across the
                business.
              </BodyParagraph>
            </div>
          </>;
      case 'complexity':
      default:
        return <>
            <PageMeta title="The problem behind the problem" description="In fintech, products and workflows are interconnected systems, not linear flows. I map the constraints before designing the interface." url="https://mintextract.design/complexity" ogImage={OG_IMAGE} data-id="element-98" />
            <PageTitle data-id="element-99">The problem behind the problem</PageTitle>
            <div className="space-y-8" data-id="element-100">
              <LeadParagraph data-id="element-101">
                The policy decisions, technological constraints, and human
                judgements that build up across origination, decisioning, and
                servicing shape what users actually experience. My work starts
                at this foundational level, before the interface.
              </LeadParagraph>
              <BodyParagraph data-id="element-102">
                At Shift, this means mapping handoffs, queue behaviours, and
                manual workarounds across Credit, Broker Operations, and
                Settlements to understand where commercial leakage occurs and
                where workflow design leads to abandonment rather than
                conversion. At Lendi Group, before redesigning Required Actions,
                I audited the existing policy rules and eliminated 285
                duplicates, raising the threshold for adding new ones entirely.
                The UI work came second. This sequence prevented months of
                downstream debt, reframed what the team was actually trying to
                solve, and ultimately saved over 13,000 broker minutes each
                month.
              </BodyParagraph>
              <div className="my-12 animate-fadeIn" data-id="element-103">
                <div className="rounded-lg overflow-hidden" style={{
                aspectRatio: '4/3'
              }} data-id="element-104">
                  <img src="https://cdn.magicpatterns.com/uploads/6ad4CNyysTrpZ6q3CrKNy5/Slide_1.svg" alt="Required Actions — 285 duplicate policy rules audited and removed before a single screen was redesigned" className="w-full h-full object-cover" data-id="element-105" />
                </div>
                <div className="mt-4 hover:translate-x-2 transition-all duration-300 inline-block group" data-id="element-106">
                  <a href="/work/required-actions" onClick={e => {
                  e.preventDefault();
                  onNavigate('work/required-actions');
                }} className="inline-flex items-center gap-2 text-fluid-sm text-[#7A8290] hover:text-[#111111] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#111111]/20 focus:ring-offset-1 focus:ring-offset-white rounded-sm whitespace-nowrap" aria-label="View case study" data-id="element-107">
                    View case study
                    <ArrowRight size={14} strokeWidth={2} className="shrink-0" aria-hidden="true" data-id="element-108" />
                  </a>
                </div>
              </div>
              <BodyParagraph data-id="element-109">
                <HighlightTag data-id="element-110">
                  My three guiding principles are: identify the real constraint
                  before proposing a solution, design for the person doing the
                  hardest version of the task, and make the rationale clear so
                  everyone in the room can challenge it, not just the designers.
                </HighlightTag>
              </BodyParagraph>

              <BodyParagraph data-id="element-111">
                These principles only hold if the people around you share them.
                At Lendi Group and Shift, I set up weekly Product Quad Think
                Tanks and biweekly Jam Forums, open to the entire
                cross-functional team, to identify friction and opportunities
                before they became assumptions. The measure was never
                attendance. It was whether the quality of questions asked in
                sprint planning improved over time. It did.
              </BodyParagraph>
            </div>
          </>;
    }
  };
  return <div className="flex-1 bg-white text-black flex flex-col min-h-screen" data-id="element-112">
      <DesignHeader onNavigate={onNavigate} onOpenMobileMenu={onOpenMobileMenu} data-id="element-113" />

      <div className="flex-1 overflow-y-auto" data-id="element-114">
        <ContentLayout data-id="element-115">{renderContent()}</ContentLayout>
      </div>
    </div>;
}